/**
 * Runs (contracts/ui-api.md "The run page", "Run history", "New run" start;
 * mission 3 credits-and-runs-api.md, mission 4 pipeline-api.md, mission 5
 * results-api.md; C-006). Types carry only the members the SPA reads.
 */
import { api, idSegment, oneOf } from '../api'
import type { Bureau } from './headerParams'
import type { TransmissionResult } from './transmissions'

/** The eight states of backend/internal/domain/run/run.go, plus any the SPA does not know yet. */
export type KnownRunState =
  | 'queued'
  | 'validating'
  | 'generating'
  | 'analyzing'
  | 'ready'
  | 'rejected'
  | 'failed'
  | 'cancelled'
export type RunState = KnownRunState | (string & {})

export const RUN_STATES: readonly KnownRunState[] = [
  'queued',
  'validating',
  'generating',
  'analyzing',
  'ready',
  'rejected',
  'failed',
  'cancelled',
]

/** Path A generates from the Trellis layout; Path B analyzes a customer-supplied Metro 2 file (U.1, U.4). */
export type RunPath = 'generate' | 'analyze'
export const RUN_PATHS: readonly RunPath[] = ['generate', 'analyze']

const TERMINAL: readonly RunState[] = ['ready', 'rejected', 'failed', 'cancelled']

/** Ready, rejected, failed and cancelled end a run; polling stops there (C10). */
export function isTerminal(state: RunState): boolean {
  return TERMINAL.includes(state)
}

export type FailureReason = 'parked' | 'upload-object-changed' | 'generation-empty' | 'analysis-timeout' | (string & {})

/** A Path A rejection: the layout validator's answer (U.2). */
export interface ValidationRejection {
  stage: 'validation'
  error_count: number
  errors_by_column: Record<string, number>
  report_url: string | null
}

export interface StructuralError {
  code: string
  detail: string
  offset: number | null
}

/** A Path B rejection: the structural pre-check's answer (U.4). */
export interface PreCheckRejection {
  stage: 'pre-check'
  error_count: number
  // mission 6 WP13: the contract allows null once the rows are gone (history_handlers.go; WP07 spine request).
  errors: StructuralError[] | null
  errors_truncated: boolean
}

export type RunRejection = ValidationRejection | PreCheckRejection

export interface RunDownloads {
  count: number
  last_at: string | null
}

/** GET /v1/runs/{id}. */
export interface Run {
  run_id: string
  upload_id: string
  path: RunPath
  state: RunState
  /** Path A only. */
  layout_version?: string
  record_count: number
  credits_reserved: number
  credits_consumed: number
  rule_set_version: string
  /** Null outside generating and analyzing. */
  progress: number | null
  deliverable: boolean
  started_at: string
  completed_at: string | null
  expires_at: string | null
  upload?: { filename: string; size_bytes: number }
  downloads: RunDownloads
  /** Failed runs only; omitted otherwise. */
  failure_reason?: FailureReason
  error_count?: number
  warn_count?: number
  info_count?: number
  /** Path A, ready. */
  generated?: { sha256: string; size: number }
  trailer?: { consistent: boolean }
  rejection: RunRejection | null
  /**
   * Mission 10 (FR-019; C-001 names the marker; WP15 T084): when the retention sweep deleted the run's Class 1
   * data. Absent or null while it is held; the run's Class 2 history stays.
   */
  class1_deleted_at?: string | null
}

/** A history entry's newest transmission (mission 7 FR-016; TX.6; C-001 named addition). */
export interface LatestTransmission {
  transmission_id: string
  bureau: Bureau
  result: TransmissionResult
  attempted_at: string
}

/** One row of GET /v1/runs. */
export interface RunSummary {
  run_id: string
  path: RunPath
  state: RunState
  record_count: number
  rule_set_version: string
  error_count: number
  warn_count: number
  info_count: number
  credits_consumed: number
  deliverable: boolean
  started_at: string
  upload: { filename: string } | null
  downloads: RunDownloads
  rejection: RunRejection | null
  failure_reason: FailureReason | null
  /** Mission 7: the newest attempt, or null when the run has none. */
  latest_transmission?: LatestTransmission | null
  /** Mission 10 (FR-019, 10/9 "Findings expired"): when the sweep deleted the run's Class 1 data. */
  class1_deleted_at?: string | null
}

export interface RunsPage {
  entries: RunSummary[]
  next_cursor?: string
}

export interface StartRunInput {
  upload_id: string
  /** YYYY-MM-DD; absent means the server default (UTC today). */
  activity_date?: string
  date_created?: string
  /** Two characters A-Z0-9; absent means the activity month. */
  cycle_identifier?: string
}

export interface RunFilters {
  state?: KnownRunState
  path?: RunPath
  cursor?: string
}

/** POST /v1/runs (B.3, P.4, SEC.8). Only the per-run fields the reporter filled are sent. */
export async function startRun(input: StartRunInput): Promise<Run> {
  idSegment(input.upload_id, 'upload id')
  const body: StartRunInput = { upload_id: input.upload_id }
  if (input.activity_date !== undefined) body.activity_date = input.activity_date
  if (input.date_created !== undefined) body.date_created = input.date_created
  if (input.cycle_identifier !== undefined) body.cycle_identifier = input.cycle_identifier
  return api<Run>('/runs', { method: 'POST', body: JSON.stringify(body) })
}

/** GET /v1/runs/{id}. */
export async function getRun(id: string): Promise<Run> {
  return api<Run>(`/runs/${idSegment(id, 'run id')}`)
}

/** GET /v1/runs?limit=50 with the filters given (NFR-005: one page of at most 50). */
export async function listRuns(filters: RunFilters): Promise<RunsPage> {
  const q = new URLSearchParams({ limit: '50' })
  if (filters.state !== undefined) q.set('state', oneOf(filters.state, RUN_STATES, 'state'))
  if (filters.path !== undefined) q.set('path', oneOf(filters.path, RUN_PATHS, 'path'))
  if (filters.cursor !== undefined) q.set('cursor', filters.cursor)
  return api<RunsPage>(`/runs?${q.toString()}`)
}

/** POST /v1/runs/{id}/cancel (D.8: a cancelled run consumes nothing). */
export async function cancelRun(id: string): Promise<Run> {
  return api<Run>(`/runs/${idSegment(id, 'run id')}/cancel`, { method: 'POST' })
}
